/**
 * THE PRODUCT'S OWN BRAND, ON THE PRODUCT PAGE.
 *
 * The owner, looking at a branded product: "why is our logo here, in the side?
 * (I mean the logo of the biz)". On a desktop the info column IS the side, and
 * the card at the bottom of it — a mark, a bold name, a chevroned "All
 * products" — is shaped exactly like a brand card. It was rendering
 * `salonLogoUrl` and the business's name, on a product whose payload carries
 * `brand: { name: 'لوريال', slug: 'lwryal', logoUrl, isOfficialImporter: true }`
 * and has carried it all along. Every public product payload, card and detail,
 * has `brand` and `brandId` — both `null` when unbranded, never absent — so
 * nothing had to be added anywhere to fix this; the page simply never looked.
 *
 * ── WHICH PICTURE, AND WHY THIS ONE ──────────────────────────────────────
 *
 * A brand has three, and they are three different shapes: `heroImageUrl` is a
 * wide banner, `coverImageUrl` is a card square, `logoUrl` is a mark. This
 * slot is a mark slot — a small identifying picture beside the brand's name —
 * so it takes `resolveBrandMarkUrl`, the mark ladder: logo first, then cover,
 * then hero. The same ladder as the strip, because the strip is a mark slot
 * too; the CARD ladder (cover-first) and the PAGE ladder (hero-first) stay
 * where their own shapes are. Falling down the ladder rather than stopping at
 * `logoUrl` is the whole of the owner's request — most brands have set only
 * some of the three, and whichever one they set has to show up here.
 *
 * ── NOT A DISC, AND NOTHING CROPPED ──────────────────────────────────────
 *
 * The salon card's mark is `border-radius: 50%; object-fit: cover`, which is
 * the exact treatment the brand strip removed a round ago for the reason the
 * owner gave then: a wide wordmark cropped into a circle is destroyed, and a
 * brand mark is an arbitrary upload. This row uses the strip's treatment
 * instead — one block-size, a free inline size under a cap, `object-fit:
 * contain`, over the same measured `--shop-logo-mat` — so a brand looks the
 * same on the product page as it does on the main page.
 *
 * ── WHAT HAPPENED TO THE SALON CARD ──────────────────────────────────────
 *
 * It stays, with its test id, because it is the only route from this page into
 * the whole catalogue: "Back" is `navigate(-1)`, which for a shared link or a
 * QR scan goes nowhere. What changes is which of the two leads. On a branded
 * product this row is the loud one and the shop card is demoted to a quiet
 * line under it; on an unbranded product nothing is added at all and the shop
 * card is exactly what it is today. The business's mark is still on the page,
 * but only ever under the business's own name — never in the brand's place.
 *
 * ── AND WHEN THERE IS NO PICTURE, THERE IS NO BOX ────────────────────────
 *
 * The strip prints the brand's name as a wordmark in that case, because the
 * strip shows nothing else — the wordmark IS the name there. Here the name is
 * already the next thing on the line, so a wordmark in the mark slot printed
 * it twice: "لوريال  لوريال / كل منتجات لوريال". Measured at 414 in Arabic
 * before this was fixed. So the slot is simply not rendered — no plate, no
 * letter in a disc (the round before this one removed those), no empty box —
 * and the row becomes the brand's name and the way to its products, which is
 * all there is to say. `dk-store`'s eleven real brands are all this case.
 *
 * The importer tick then has nothing to pin to, so it goes inline beside the
 * name instead, which is where the "official importer of X" sentence already
 * puts it. Same component, same geometry, one of two anchors.
 *
 * A dead upload takes the same path: an `<img>` whose file was deleted renders
 * the browser's broken-image glyph, which is worse than no picture at all, so
 * the `error` event drops the row to the no-picture form.
 *
 * Class names checked against `modal-fixes.css`'s `[class*="panel"]` /
 * `[class*="overlay"]` / `[class*="popup"]` / `[class*="modal"]` wildcards.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { getBasePath } from '../../utils/domainMode';
import { brandName, brandPath, resolveBrandMarkUrl } from '../../utils/productBrands';
import OfficialImporterTick from '../Shop/OfficialImporterTick';
import styles from './styles.module.css';

export default function ProductBrandRow({ brand, salonSlug, language, t, isRtl }) {
  const [broken, setBroken] = useState(false);
  // Hooks first, always: an unbranded product must not change the hook order.
  if (!brand?.id && !brand?.name) return null;

  const name = brandName(brand, language);
  const src = resolveBrandMarkUrl(brand);
  const showImage = !!src && !broken;
  const href = brandPath(getBasePath(salonSlug), brand);
  const Chevron = isRtl ? FiChevronLeft : FiChevronRight;

  /* THE SAME COMPONENT THE STRIP, THE CARDS AND THE BRAND PAGE USE — the owner
   * asked for this mark "here and everywhere", and everywhere means one
   * component rather than five things that resemble each other. Pinned to the
   * artwork when there is artwork; inline beside the name when there is not. */
  const tick = brand.isOfficialImporter ? (
    <OfficialImporterTick
      t={t}
      pinned={showImage}
      testId="product-detail-brand-importer"
      className={showImage ? undefined : styles.brandRowNameTick}
    />
  ) : null;

  const body = (
    <>
      {showImage && (
        <span className={styles.brandRowMark}>
          <img
            src={src}
            alt=""
            className={styles.brandRowMarkImg}
            data-testid="product-detail-brand-mark"
            loading="lazy"
            decoding="async"
            onError={() => setBroken(true)}
          />
          {tick}
        </span>
      )}
      <span className={styles.brandRowText}>
        <span className={styles.brandRowName}>
          {name}
          {!showImage && tick}
        </span>
        <span className={styles.brandRowLink}>
          {t('productDetail.brandAllProducts', {
            defaultValue: 'All products by {{brand}}',
            brand: name,
          })}
          <Chevron aria-hidden />
        </span>
      </span>
    </>
  );

  /* A brand with neither slug nor id cannot be linked — `/shop/brands/undefined`
   * is a 404 with the brand's name on it. It still gets to be shown. */
  if (!href) {
    return (
      <div className={styles.brandRow} data-testid="product-detail-brand" data-brand-id={brand.id}>
        {body}
      </div>
    );
  }

  return (
    <Link
      to={href}
      className={styles.brandRow}
      data-testid="product-detail-brand"
      data-brand-id={brand.id}
    >
      {body}
    </Link>
  );
}
